import React, { useState, useMemo, useCallback, memo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// Import bookmaker logos
import sportsbetLogo from '../../assets/sb.png';
import tabLogo from '../../assets/tab.jpg';
import pointsbetLogo from '../../assets/pb.png';
import dabbleLogo from '../../assets/dabble.webp';
import nedsLogo from '../../assets/neds.jpg';
import betrightLogo from '../../assets/betright.jpg';
import tabtouchLogo from '../../assets/tabtouch.webp';
import bet365Logo from '../../assets/bet365.png';
import betmakersLogo from '../../assets/betmakers.png';
import ladbrokesLogo from '../../assets/ladbrokes.webp';
import betrLogo from '../../assets/betr.jpg';
import picklebetLogo from '../../assets/picklebet.jpeg';
import playupLogo from '../../assets/playup.png';
import unibetLogo from '../../assets/unibet.webp';
import amusedLogo from '../../assets/amused.jpg';

// Bookmaker options shared by forms
const BOOKIE_OPTIONS = ['Sportsbet', 'TAB', 'Pointsbet', 'Dabble', 'Neds', 'Betright', 'TABtouch', 'Bet365', 'Ladbrokes', 'Betmakers', 'Betr', 'Picklebet', 'PlayUp', 'Unibet', 'Other'];

// Unified Add Bet(s) Form - handles both single and bulk adding
const AddBetsForm = memo(function AddBetsForm({ onSubmit, onCancel, sportOptions, isSubmitting, displayMode = 'dollars', unitSize = 25, getUnitSizeForDate, advancedUnitSettings = false }) {
    const isUnitMode = displayMode === 'units';

    // Get the effective unit size for a given date
    const getEffectiveUnitSize = (date) => {
        if (advancedUnitSettings && getUnitSizeForDate) {
            return getUnitSizeForDate(date);
        }
        return unitSize;
    };

    const createEmptyBet = () => ({
        id: `temp-${Date.now()}-${Math.random()}`,
        date: new Date().toISOString().split('T')[0],
        sport: sportOptions[0]?.value || 'nba',
        game: '',
        bet: '',
        odds: '',
        stake: '',      // In unit mode this is units, in dollar mode this is dollars
        payout: '',
        bookie: '',
        result: 'pending',
        bonus_bet: false
    });

    const [bets, setBets] = useState([createEmptyBet()]);

    const updateBet = (index, field, value) => {
        setBets(prev => prev.map((bet, i) => {
            if (i !== index) return bet;
            const updated = { ...bet, [field]: value };
            // Recalculate payout when odds, stake, bonus_bet, or date changes (date affects unit size)
            if (field === 'odds' || field === 'stake' || field === 'bonus_bet' || field === 'date') {
                const odds = field === 'odds' ? parseFloat(value) : parseFloat(bet.odds);
                const stakeInput = field === 'stake' ? parseFloat(value) : parseFloat(bet.stake);
                const isBonus = field === 'bonus_bet' ? value : bet.bonus_bet;
                const date = field === 'date' ? value : bet.date;
                if (odds && stakeInput) {
                    // Convert units to dollars for payout calculation
                    const stakeDollars = isUnitMode ? stakeInput * getEffectiveUnitSize(date) : stakeInput;
                    const rawPayout = odds * stakeDollars;
                    const payout = isBonus ? rawPayout - stakeDollars : rawPayout;
                    // Display payout in units if in unit mode
                    updated.payout = isUnitMode
                        ? (payout / getEffectiveUnitSize(date)).toFixed(2)
                        : payout.toFixed(2);
                }
            }
            return updated;
        }));
    };

    const addRow = () => setBets(prev => [...prev, createEmptyBet()]);
    const removeRow = (index) => {
        if (bets.length <= 1) return;
        setBets(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        const validBets = bets.filter(bet => bet.game && bet.bet && bet.odds && bet.stake && bet.bookie && bet.sport && bet.sport !== '__other__');
        if (validBets.length === 0) return;
        
        // Always submit in dollars - convert from units if needed
        const betsData = validBets.map(bet => {
            const stakeInput = parseFloat(bet.stake);
            const effectiveUnitSize = getEffectiveUnitSize(bet.date);
            const stakeDollars = isUnitMode ? stakeInput * effectiveUnitSize : stakeInput;
            const payoutDollars = isUnitMode ? parseFloat(bet.payout) * effectiveUnitSize : parseFloat(bet.payout);
            return {
                date: bet.date,
                sport: bet.sport || 'nba',
                game: bet.game,
                bet: bet.bet,
                odds: parseFloat(bet.odds),
                stake: stakeDollars,
                payout: payoutDollars || 0,
                bookie: bet.bookie,
                result: bet.result,
                bonus_bet: bet.bonus_bet || false
            };
        });

        const result = await onSubmit(betsData);
        if (result?.success) {
            setBets([createEmptyBet()]);
        }
    };

    const validCount = bets.filter(b => b.game && b.bet && b.odds && b.stake && b.bookie && b.sport && b.sport !== '__other__').length;

    return (
        <div className="p-4 md:p-6 bg-slate-800/50 border-b border-slate-700/50">
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                {bets.map((bet, index) => (
                    <div key={bet.id} className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-[1fr_1fr_2fr_2fr_1fr_1fr_1fr_1fr_1fr_auto_auto] gap-x-2 gap-y-2 md:gap-y-2 lg:gap-y-0 items-end p-3 md:p-3 bg-slate-900/50 rounded-lg">
                        {/* Column headers - only on first row, lg screens */}
                        {index === 0 && <>
                            <span className="hidden lg:block text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">Date</span>
                            <span className="hidden lg:block text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">Sport</span>
                            <span className="hidden lg:block text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">Game</span>
                            <span className="hidden lg:block text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">Bet</span>
                            <span className="hidden lg:block text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">Odds</span>
                            <span className="hidden lg:block text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">{isUnitMode ? 'Stake (U)' : 'Stake'}</span>
                            <span className="hidden lg:block text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">{isUnitMode ? 'Payout (U)' : 'Payout'}</span>
                            <span className="hidden lg:block text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">Bookie</span>
                            <span className="hidden lg:block text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">Result</span>
                            <span className="hidden lg:block"></span>
                            <span className="hidden lg:block"></span>
                        </>}
                        <input type="date" value={bet.date} onChange={(e) => updateBet(index, 'date', e.target.value)} className="px-2 py-2 md:py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-emerald-500 [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:saturate-100 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:sepia [&::-webkit-calendar-picker-indicator]:saturate-[10000%] [&::-webkit-calendar-picker-indicator]:hue-rotate-[115deg] [&::-webkit-calendar-picker-indicator]:cursor-pointer" />
                        {bet.sport === '__other__' || (bet.sport && !sportOptions.some(s => s.value === bet.sport) && bet.customSport !== undefined) ? (
                            <div className="flex gap-1">
                                <input
                                    type="text"
                                    placeholder="Sport name"
                                    value={bet.customSport || ''}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setBets(prev => prev.map((b, i) => i !== index ? b : { ...b, customSport: val, sport: val.toLowerCase().trim() || '__other__' }));
                                    }}
                                    autoFocus
                                    className="flex-1 min-w-0 px-2 py-2 md:py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                                />
                                <button
                                    onClick={() => setBets(prev => prev.map((b, i) => i !== index ? b : { ...b, sport: sportOptions[0]?.value || 'nba', customSport: undefined }))}
                                    className="px-1.5 text-slate-400 hover:text-white transition-colors"
                                    title="Back to dropdown"
                                >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                        ) : (
                            <select value={bet.sport} onChange={(e) => {
                                if (e.target.value === '__other__') {
                                    setBets(prev => prev.map((b, i) => i !== index ? b : { ...b, sport: '__other__', customSport: '' }));
                                } else {
                                    updateBet(index, 'sport', e.target.value);
                                }
                            }} className="px-2 py-2 md:py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-emerald-500">
                                {sportOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                <option value="__other__">Other...</option>
                            </select>
                        )}
                        <input type="text" placeholder="Game" value={bet.game} onChange={(e) => updateBet(index, 'game', e.target.value)} className="px-2 py-2 md:py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500" />
                        <input type="text" placeholder="Bet" value={bet.bet} onChange={(e) => updateBet(index, 'bet', e.target.value)} className="px-2 py-2 md:py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500" />
                        <input type="number" step="0.01" placeholder="Odds" value={bet.odds} onChange={(e) => updateBet(index, 'odds', e.target.value)} className="px-2 py-2 md:py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500" />
                        <input type="number" step="0.01" placeholder={bet.bonus_bet ? (isUnitMode ? "Bonus (U)" : "Bonus Amt") : (isUnitMode ? "Units" : "Stake")} value={bet.stake} onChange={(e) => updateBet(index, 'stake', e.target.value)} className="px-2 py-2 md:py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500" />
                        <input type="number" step="0.01" placeholder={isUnitMode ? "Payout (U)" : "Payout"} value={bet.payout} readOnly className="px-2 py-2 md:py-1.5 bg-slate-900/50 border border-slate-700 rounded-lg text-xs text-slate-400 placeholder-slate-500 focus:outline-none cursor-not-allowed" title={bet.bonus_bet ? "Auto-calculated: (Odds × Stake) - Stake" : "Auto-calculated: Odds × Stake"} />
                        <select value={bet.bookie} onChange={(e) => updateBet(index, 'bookie', e.target.value)} className="px-2 py-2 md:py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-emerald-500">
                            <option value="">Bookie</option>
                            {BOOKIE_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                        <select value={bet.result} onChange={(e) => updateBet(index, 'result', e.target.value)} className="px-2 py-2 md:py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-emerald-500">
                            <option value="pending">Pending</option>
                            <option value="win">Win</option>
                            <option value="loss">Loss</option>
                            {!bet.bonus_bet && <option value="cash out">Cash Out</option>}
                        </select>
                        <label className="flex items-center gap-1.5 cursor-pointer justify-self-center self-center" title="Bonus Bet">
                            <input
                                type="checkbox"
                                checked={bet.bonus_bet}
                                onChange={(e) => updateBet(index, 'bonus_bet', e.target.checked)}
                                className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0 cursor-pointer"
                            />
                            <span className="text-[10px] text-slate-400 whitespace-nowrap">Bonus</span>
                        </label>
                        <button onClick={() => removeRow(index)} disabled={bets.length <= 1} className="p-1.5 text-slate-400 hover:text-red-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed justify-self-center self-center" title="Remove row">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                    </div>
                ))}
            </div>
            
            <div className="mt-4 flex items-center justify-between">
                <button onClick={addRow} className="px-3 py-1.5 text-emerald-400 hover:text-emerald-300 text-sm font-medium flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                    Add Another Bet
                </button>
                <div className="flex gap-2">
                    <button onClick={onCancel} disabled={isSubmitting} className="px-4 py-2 text-slate-400 hover:text-white transition-colors text-sm disabled:opacity-50">Cancel</button>
                    <button onClick={handleSubmit} disabled={isSubmitting || validCount === 0} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                        {isSubmitting ? (<><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Adding...</>) : `Add ${validCount} Bet${validCount !== 1 ? 's' : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
});

function BetTracker({ bets, onAddBet, onBulkAddBets, onUpdateBet, onDeleteBet, onReorderBets, displayMode = 'dollars', unitSize = 25, getUnitSizeForDate, advancedUnitSettings = false, isSaving = false, sportOptions = [] }) {
    const [isAddingBets, setIsAddingBets] = useState(false);
    const [editingBetId, setEditingBetId] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
    const [filterResult, setFilterResult] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [deletingBetId, setDeletingBetId] = useState(null);

    const [editBet, setEditBet] = useState(null);
    
    // Drag and drop state
    const [draggedBet, setDraggedBet] = useState(null);
    const [dropTargetId, setDropTargetId] = useState(null);
    const [isReordering, setIsReordering] = useState(false);
    
    // Cash out modal state
    const [showCashOutModal, setShowCashOutModal] = useState(false);
    const [cashOutBet, setCashOutBet] = useState(null);
    const [cashOutAmount, setCashOutAmount] = useState('');
    const cashOutInputRef = useRef(null);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (showCashOutModal) {
            document.body.style.overflow = 'hidden';
            // Focus input after modal renders
            setTimeout(() => {
                cashOutInputRef.current?.focus({ preventScroll: true });
            }, 50);
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [showCashOutModal]);

    // Handler for the unified add bets form
    const handleAddBetsSubmit = useCallback(async (betsData) => {
        // Use bulk add for all submissions (works for single or multiple bets)
        const result = await onBulkAddBets(betsData);
        if (result?.success) {
            setIsAddingBets(false);
        }
        return result;
    }, [onBulkAddBets]);

    const handleCancelAdd = useCallback(() => setIsAddingBets(false), []);

    // Calculate cumulative P/L in chronological order (oldest to newest)
    // Returns both dollar cumulative and unit cumulative (for advanced unit settings)
    const cumulativePLMaps = useMemo(() => {
        const dollarMap = {};
        const unitMap = {};
        let cumulativeDollars = 0;
        let cumulativeUnits = 0;
        
        // Sort bets chronologically (date ASC, sort_order ASC) for correct cumulative calculation
        // Fallback to id if sort_order is not set (legacy data)
        const chronologicalBets = [...bets].sort((a, b) => {
            const dateCompare = new Date(a.date) - new Date(b.date);
            if (dateCompare !== 0) return dateCompare;
            // Use sort_order if available, otherwise fallback to id
            const aSortOrder = a.sort_order ?? a.id;
            const bSortOrder = b.sort_order ?? b.id;
            return aSortOrder - bSortOrder;
        });
        
        // Calculate cumulative P/L from oldest to newest
        chronologicalBets.forEach(bet => {
            cumulativeDollars += bet.pl;
            dollarMap[bet.id] = cumulativeDollars;
            
            // Calculate units using the bet's specific unit size
            const betUnitSize = advancedUnitSettings && getUnitSizeForDate 
                ? getUnitSizeForDate(bet.date) 
                : unitSize;
            cumulativeUnits += bet.pl / betUnitSize;
            unitMap[bet.id] = cumulativeUnits;
        });
        
        return { dollarMap, unitMap };
    }, [bets, advancedUnitSettings, getUnitSizeForDate, unitSize]);

    // Search-filtered bets (for totals - ignores result filter)
    const searchFilteredBets = useMemo(() => {
        if (!searchTerm) return bets;
        const term = searchTerm.toLowerCase();
        return bets.filter(b => 
            b.game.toLowerCase().includes(term) ||
            b.bet.toLowerCase().includes(term) ||
            b.bookie.toLowerCase().includes(term)
        );
    }, [bets, searchTerm]);

    // Sorting and filtering
    const processedBets = useMemo(() => {
        let filtered = [...bets];

        // Filter by result
        if (filterResult !== 'all') {
            filtered = filtered.filter(b => b.result === filterResult);
        }

        // Search
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(b => 
                b.game.toLowerCase().includes(term) ||
                b.bet.toLowerCase().includes(term) ||
                b.bookie.toLowerCase().includes(term)
            );
        }

        // Sort
        filtered.sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            if (sortConfig.key === 'date') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            }

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            
            // When primary values are equal (especially for dates), use sort_order as secondary sort
            // Match the direction: ASC = lower sort_order first, DESC = higher sort_order first
            const aSortOrder = a.sort_order ?? a.id;
            const bSortOrder = b.sort_order ?? b.id;
            return sortConfig.direction === 'asc' ? aSortOrder - bSortOrder : bSortOrder - aSortOrder;
        });

        // Add pre-calculated cumulative P/L (based on chronological order, not affected by filters)
        // Use dollar or unit cumulative based on display mode
        const withCumulative = filtered.map(bet => ({
            ...bet,
            cumulativePL: cumulativePLMaps.dollarMap[bet.id],
            cumulativePLUnits: cumulativePLMaps.unitMap[bet.id]
        }));

        return withCumulative;
    }, [bets, sortConfig, filterResult, searchTerm, cumulativePLMaps]);

    const handleSort = useCallback((key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
    }, []);

    const [isUpdating, setIsUpdating] = useState(false);
    const [updatingResultBetId, setUpdatingResultBetId] = useState(null);

    // Quick result change handler - update result without entering edit mode
    const handleQuickResultChange = async (bet, newResult) => {
        if (newResult === bet.result) return;
        
        // For cash out, show modal to get the cash out amount
        if (newResult === 'cash out') {
            setCashOutBet(bet);
            setCashOutAmount('');
            setShowCashOutModal(true);
            return;
        }
        
        setUpdatingResultBetId(bet.id);
        
        // When changing to win/loss/pending, recalculate the original payout (stake * odds)
        // This is important when re-resulting from cash out, as payout was overwritten with cash out amount
        // For bonus bets: payout = (odds * stake) - stake
        const originalPayout = bet.bonus_bet 
            ? (bet.stake * bet.odds) - bet.stake 
            : bet.stake * bet.odds;
        
        // Calculate new P/L based on result using the original payout
        // Bonus bet: win P/L = payout (stake was free), loss P/L = 0 (no real money lost)
        // Normal bet: win P/L = payout - stake, loss P/L = -stake
        const newPL = bet.bonus_bet
            ? (newResult === 'win' ? originalPayout : 0)
            : (newResult === 'win' ? originalPayout - bet.stake : 
               newResult === 'loss' ? -bet.stake : 0);
        
        const betData = {
            id: bet.id,
            date: bet.date,
            sport: bet.sport || 'nba',
            game: bet.game,
            bet: bet.bet,
            odds: bet.odds,
            stake: bet.stake,
            payout: originalPayout, // Restore original payout when re-resulting
            bookie: bet.bookie,
            result: newResult,
            pl: newPL,
            bonus_bet: bet.bonus_bet || false
        };

        await onUpdateBet(betData);
        setUpdatingResultBetId(null);
    };

    // Handle cash out confirmation with amount
    const handleCashOutConfirm = async () => {
        if (!cashOutBet || !cashOutAmount) return;
        
        const cashOutValue = parseFloat(cashOutAmount);
        if (isNaN(cashOutValue) || cashOutValue < 0) return;
        
        setUpdatingResultBetId(cashOutBet.id);
        setShowCashOutModal(false);
        
        // P/L: bonus bet = cash out amount (stake was free), normal = cash out amount - stake
        const newPL = cashOutBet.bonus_bet ? cashOutValue : cashOutValue - cashOutBet.stake;
        
        const betData = {
            id: cashOutBet.id,
            date: cashOutBet.date,
            sport: cashOutBet.sport || 'nba',
            game: cashOutBet.game,
            bet: cashOutBet.bet,
            odds: cashOutBet.odds,
            stake: cashOutBet.stake,
            payout: cashOutValue, // Store the cash out amount as payout
            bookie: cashOutBet.bookie,
            result: 'cash out',
            pl: newPL,
            bonus_bet: cashOutBet.bonus_bet || false
        };

        await onUpdateBet(betData);
        setUpdatingResultBetId(null);
        setCashOutBet(null);
        setCashOutAmount('');
    };

    const handleCashOutCancel = () => {
        setShowCashOutModal(false);
        setCashOutBet(null);
        setCashOutAmount('');
    };

    const handleUpdateBet = async () => {
        if (!editBet) return;
        
        setIsUpdating(true);
        
        const betData = {
            id: editBet.id,
            date: editBet.date,
            sport: editBet.sport || 'nba',
            game: editBet.game,
            bet: editBet.bet,
            odds: parseFloat(editBet.odds),
            stake: parseFloat(editBet.stake),
            payout: parseFloat(editBet.payout) || 0,
            bookie: editBet.bookie,
            result: editBet.result,
            bonus_bet: editBet.bonus_bet || false
        };

        const result = await onUpdateBet(betData);
        
        if (result?.success) {
            setEditingBetId(null);
            setEditBet(null);
        }
        
        setIsUpdating(false);
    };

    const handleDeleteBet = async (betId) => {
        setDeletingBetId(betId);
        await onDeleteBet(betId);
        setDeletingBetId(null);
    };

    const startEditing = (bet) => {
        setEditingBetId(bet.id);
        setEditBet({ ...bet });
    };

    // Group bets by date for reordering validation
    const betsByDate = useMemo(() => {
        const grouped = {};
        bets.forEach(bet => {
            if (!grouped[bet.date]) {
                grouped[bet.date] = [];
            }
            grouped[bet.date].push(bet);
        });
        // Sort each group by sort_order
        Object.keys(grouped).forEach(date => {
            grouped[date].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        });
        return grouped;
    }, [bets]);

    // Drag handlers
    const handleDragStart = useCallback((e, bet) => {
        // Don't allow dragging if filtering or searching (reorder only works on full date groups)
        if (filterResult !== 'all' || searchTerm) {
            e.preventDefault();
            return;
        }
        setDraggedBet(bet);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', bet.id.toString());
        // Add a slight delay to allow the drag image to be set
        setTimeout(() => {
            e.target.closest('tr')?.classList.add('opacity-50');
        }, 0);
    }, [filterResult, searchTerm]);

    const handleDragEnd = useCallback((e) => {
        e.target.closest('tr')?.classList.remove('opacity-50');
        setDraggedBet(null);
        setDropTargetId(null);
    }, []);

    const handleDragOver = useCallback((e, bet) => {
        e.preventDefault();
        if (!draggedBet || draggedBet.id === bet.id) return;
        
        // Only allow dropping on bets with the same date
        if (draggedBet.date !== bet.date) {
            e.dataTransfer.dropEffect = 'none';
            return;
        }
        
        e.dataTransfer.dropEffect = 'move';
        setDropTargetId(bet.id);
    }, [draggedBet]);

    const handleDragLeave = useCallback(() => {
        setDropTargetId(null);
    }, []);

    const handleDrop = useCallback(async (e, targetBet) => {
        e.preventDefault();
        setDropTargetId(null);
        
        if (!draggedBet || draggedBet.id === targetBet.id || draggedBet.date !== targetBet.date) {
            setDraggedBet(null);
            return;
        }
        
        const date = draggedBet.date;
        const betsForDate = [...(betsByDate[date] || [])];
        
        // Find current positions
        const draggedIndex = betsForDate.findIndex(b => b.id === draggedBet.id);
        const targetIndex = betsForDate.findIndex(b => b.id === targetBet.id);
        
        if (draggedIndex === -1 || targetIndex === -1) {
            setDraggedBet(null);
            return;
        }
        
        // Remove dragged bet and insert at target position
        const [removed] = betsForDate.splice(draggedIndex, 1);
        betsForDate.splice(targetIndex, 0, removed);
        
        // Create new order with sequential sort_order values
        const betOrders = betsForDate.map((bet, index) => ({
            id: bet.id,
            sort_order: index + 1
        }));
        
        setDraggedBet(null);
        setIsReordering(true);
        
        // Call API to persist the new order
        if (onReorderBets) {
            await onReorderBets(date, betOrders);
        }
        
        setIsReordering(false);
    }, [draggedBet, betsByDate, onReorderBets]);

    // Check if reordering is allowed (no filters active)
    const canReorder = filterResult === 'all' && !searchTerm && sortConfig.key === 'date';

    const formatCurrency = (value, betDate = null) => {
        if (value === undefined || value === null) return '-';
        if (displayMode === 'units') {
            const betUnitSize = advancedUnitSettings && getUnitSizeForDate && betDate 
                ? getUnitSizeForDate(betDate) 
                : unitSize;
            const units = value / betUnitSize;
            const prefix = units >= 0 ? '+' : '-';
            return `${prefix}${Math.abs(units).toFixed(1)}U`;
        }
        const prefix = value >= 0 ? '+$' : '-$';
        return `${prefix}${Math.abs(value).toFixed(2)}`;
    };

    // Format cumulative P/L - uses pre-calculated unit values for advanced mode
    const formatCumulativePL = (dollarValue, unitValue) => {
        if (dollarValue === undefined || dollarValue === null) return '-';
        if (displayMode === 'units') {
            // Use the pre-calculated cumulative units value directly
            const units = unitValue !== undefined ? unitValue : dollarValue / unitSize;
            const prefix = units >= 0 ? '+' : '-';
            return `${prefix}${Math.abs(units).toFixed(1)}U`;
        }
        const prefix = dollarValue >= 0 ? '+$' : '-$';
        return `${prefix}${Math.abs(dollarValue).toFixed(2)}`;
    };

    // Get cumulative P/L value for coloring (uses appropriate value based on mode)
    const getCumulativePLValue = (dollarValue, unitValue) => {
        if (displayMode === 'units') {
            return unitValue !== undefined ? unitValue : dollarValue / unitSize;
        }
        return dollarValue;
    };

    const formatStaked = (value, betDate = null) => {
        if (value === undefined || value === null) return '-';
        if (displayMode === 'units') {
            const betUnitSize = advancedUnitSettings && getUnitSizeForDate && betDate 
                ? getUnitSizeForDate(betDate) 
                : unitSize;
            const units = value / betUnitSize;
            return `${units.toFixed(1)}U`;
        }
        return `$${value.toFixed(2)}`;
    };

    // Format total for footer (calculates units per bet when in advanced mode)
    const formatTotalStaked = (betsList) => {
        if (displayMode === 'units') {
            const totalUnits = betsList.reduce((sum, bet) => {
                const betUnitSize = advancedUnitSettings && getUnitSizeForDate 
                    ? getUnitSizeForDate(bet.date) 
                    : unitSize;
                return sum + (bet.stake / betUnitSize);
            }, 0);
            return `${totalUnits.toFixed(1)}U`;
        }
        return `$${betsList.reduce((sum, bet) => sum + bet.stake, 0).toFixed(2)}`;
    };

    const formatTotalPL = (betsList) => {
        if (displayMode === 'units') {
            const totalUnits = betsList.reduce((sum, bet) => {
                const betUnitSize = advancedUnitSettings && getUnitSizeForDate 
                    ? getUnitSizeForDate(bet.date) 
                    : unitSize;
                return sum + (bet.pl / betUnitSize);
            }, 0);
            const prefix = totalUnits >= 0 ? '+' : '-';
            return `${prefix}${Math.abs(totalUnits).toFixed(1)}U`;
        }
        const total = betsList.reduce((sum, bet) => sum + bet.pl, 0);
        const prefix = total >= 0 ? '+$' : '-$';
        return `${prefix}${Math.abs(total).toFixed(2)}`;
    };

    const getTotalPLValue = (betsList) => {
        if (displayMode === 'units') {
            return betsList.reduce((sum, bet) => {
                const betUnitSize = advancedUnitSettings && getUnitSizeForDate 
                    ? getUnitSizeForDate(bet.date) 
                    : unitSize;
                return sum + (bet.pl / betUnitSize);
            }, 0);
        }
        return betsList.reduce((sum, bet) => sum + bet.pl, 0);
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
        });
    };

    const SortIcon = ({ column }) => {
        if (sortConfig.key !== column) {
            return (
                <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
            );
        }
        return sortConfig.direction === 'desc' ? (
            <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
        ) : (
            <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
            </svg>
        );
    };

    const bookieOptions = ['Sportsbet', 'TAB', 'Pointsbet', 'Dabble', 'Neds', 'Betright', 'TABtouch', 'Bet365', 'Ladbrokes', 'Betmakers', 'Betr', 'Picklebet', 'PlayUp', 'Unibet', 'Amused', 'Other'];
    
    // Bookmaker logo mapping
    const bookieLogos = {
        'Sportsbet': sportsbetLogo,
        'TAB': tabLogo,
        'Pointsbet': pointsbetLogo,
        'Dabble': dabbleLogo,
        'Neds': nedsLogo,
        'Betright': betrightLogo,
        'TABtouch': tabtouchLogo,
        'Bet365': bet365Logo,
        'bet365': bet365Logo,
        'Ladbrokes': ladbrokesLogo,
        'Betmakers': betmakersLogo,
        'Betr': betrLogo,
        'Picklebet': picklebetLogo,
        'PlayUp': playupLogo,
        'Unibet': unibetLogo,
        'Amused': amusedLogo,
        'amused': amusedLogo,
    };

    return (
        <div className="bg-slate-900/50 rounded-2xl border border-slate-700/50 overflow-hidden relative">
            {/* Reordering overlay */}
            {isReordering && (
                <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-2xl">
                    <div className="flex items-center gap-3 bg-slate-800 px-6 py-4 rounded-xl border border-slate-700 shadow-xl">
                        <div className="w-5 h-5 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin"></div>
                        <span className="text-white font-medium">Reordering bets...</span>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="p-4 md:p-6 border-b border-slate-700/50">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-white">Bet Log</h3>
                        {canReorder && (
                            <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 bg-slate-800/50 rounded-lg border border-slate-700/50">
                                <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                </svg>
                                <span className="text-xs text-slate-400">Drag to reorder within same date</span>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        {/* Search */}
                        <div className="relative">
                            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Search bets..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 w-full sm:w-48"
                            />
                        </div>

                        {/* Filter */}
                        <select
                            value={filterResult}
                            onChange={(e) => setFilterResult(e.target.value)}
                            className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-emerald-500"
                        >
                            <option value="all">All Results</option>
                            <option value="win">Wins</option>
                            <option value="loss">Losses</option>
                            <option value="cash out">Cashed Out</option>
                            <option value="pending">Pending</option>
                        </select>

                        {/* Add Bet(s) Button */}
                        <button
                            onClick={() => setIsAddingBets(true)}
                            disabled={isSaving}
                            className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg text-sm font-medium hover:from-emerald-500 hover:to-teal-500 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                            </svg>
                            Add Bet(s)
                        </button>
                    </div>
                </div>
            </div>

            {/* Add Bet(s) Form - Unified form for single or multiple bets */}
            {isAddingBets && (
                <AddBetsForm
                    onSubmit={handleAddBetsSubmit}
                    onCancel={handleCancelAdd}
                    sportOptions={sportOptions}
                    isSubmitting={isSaving}
                    displayMode={displayMode}
                    unitSize={unitSize}
                    getUnitSizeForDate={getUnitSizeForDate}
                    advancedUnitSettings={advancedUnitSettings}
                />
            )}

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-800/50">
                        <tr>
                            {/* Drag handle column */}
                            <th className="w-[40px] px-2 py-3">
                                {canReorder && (
                                    <div className="flex items-center justify-center" title="Drag to reorder bets within the same date">
                                        <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                        </svg>
                                    </div>
                                )}
                            </th>
                            {[
                                { key: 'date', label: 'Date', width: 'w-[100px]' },
                                { key: 'game', label: 'Game', width: 'min-w-[150px]' },
                                { key: 'bet', label: 'Bet', width: '' },
                                { key: 'odds', label: 'Odds', width: 'w-[80px]' },
                                { key: 'stake', label: 'Stake', width: 'w-[90px]' },
                                { key: 'payout', label: 'Payout', width: 'w-[90px]' },
                                { key: 'bookie', label: 'Bookie', width: 'w-[80px]' },
                                { key: 'result', label: 'Result', width: 'w-[100px]' },
                                { key: 'pl', label: 'P/L', width: 'w-[90px]' },
                                { key: 'cumulativePL', label: 'Cumulative P/L', width: 'w-[120px]', noSort: true }
                            ].map(col => (
                                <th 
                                    key={col.key}
                                    onClick={() => !col.noSort && handleSort(col.key)}
                                    className={`px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider transition-colors ${
                                        col.key === 'bookie' ? 'text-center' : 'text-left'
                                    } ${col.width || ''} ${!col.noSort ? 'cursor-pointer hover:text-white' : ''}`}
                                >
                                    <div className={`flex items-center gap-1 ${col.key === 'bookie' ? 'justify-center' : ''}`}>
                                        {col.label}
                                        <SortIcon column={col.key} />
                                    </div>
                                </th>
                            ))}
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50">
                        {processedBets.length === 0 ? (
                            <tr>
                                <td colSpan="12" className="px-4 py-8 text-center text-slate-500">
                                    No bets found. Add your first bet to get started!
                                </td>
                            </tr>
                        ) : (
                            processedBets.map((bet) => (
                                <tr 
                                    key={bet.id}
                                    draggable={canReorder && editingBetId !== bet.id}
                                    onDragStart={(e) => handleDragStart(e, bet)}
                                    onDragEnd={handleDragEnd}
                                    onDragOver={(e) => handleDragOver(e, bet)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, bet)}
                                    className={`transition-all ${
                                        canReorder && editingBetId !== bet.id ? 'cursor-grab active:cursor-grabbing' : ''
                                    } ${
                                        dropTargetId === bet.id 
                                            ? 'bg-emerald-500/20 border-t-2 border-emerald-500' 
                                            : draggedBet?.id === bet.id
                                                ? 'opacity-50'
                                                : 'hover:bg-slate-800/30'
                                    } ${
                                        bet.result === 'win' 
                                            ? 'bg-emerald-500/5' 
                                            : bet.result === 'loss' 
                                                ? 'bg-red-500/5' 
                                                : bet.result === 'cash out'
                                                    ? 'bg-blue-500/5'
                                                    : ''
                                    }`}
                                >
                                    {editingBetId === bet.id ? (
                                        // Edit mode
                                        <>
                                            {/* Empty drag handle cell in edit mode */}
                                            <td className="w-[40px] px-2 py-3"></td>
                                            <td className="px-2 py-2">
                                                <input
                                                    type="date"
                                                    value={editBet.date}
                                                    onChange={(e) => setEditBet(prev => ({ ...prev, date: e.target.value }))}
                                                    className="px-2 py-1 bg-slate-900 border border-slate-700 rounded text-sm text-white focus:outline-none focus:border-emerald-500 w-full min-w-0 [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:saturate-100 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:sepia [&::-webkit-calendar-picker-indicator]:saturate-[10000%] [&::-webkit-calendar-picker-indicator]:hue-rotate-[115deg] [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                                />
                                            </td>
                                            <td className="px-2 py-2">
                                                <input
                                                    type="text"
                                                    value={editBet.game}
                                                    onChange={(e) => setEditBet(prev => ({ ...prev, game: e.target.value }))}
                                                    className="px-2 py-1 bg-slate-900 border border-slate-700 rounded text-sm text-white focus:outline-none focus:border-emerald-500 w-full min-w-0"
                                                />
                                            </td>
                                            <td className="px-2 py-2">
                                                <input
                                                    type="text"
                                                    value={editBet.bet}
                                                    onChange={(e) => setEditBet(prev => ({ ...prev, bet: e.target.value }))}
                                                    className="px-2 py-1 bg-slate-900 border border-slate-700 rounded text-sm text-white focus:outline-none focus:border-emerald-500 w-full min-w-0"
                                                />
                                            </td>
                                            <td className="px-2 py-2">
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={editBet.odds}
                                                    onChange={(e) => {
                                                        const odds = e.target.value;
                                                        const stake = parseFloat(editBet.stake) || 0;
                                                        const isBonus = editBet.bonus_bet;
                                                        const payout = odds && stake 
                                                            ? (isBonus ? (parseFloat(odds) * stake - stake).toFixed(2) : (parseFloat(odds) * stake).toFixed(2))
                                                            : editBet.payout;
                                                        setEditBet(prev => ({ ...prev, odds, payout }));
                                                    }}
                                                    className="px-2 py-1 bg-slate-900 border border-slate-700 rounded text-sm text-white focus:outline-none focus:border-emerald-500 w-full min-w-0"
                                                />
                                            </td>
                                            <td className="px-2 py-2">
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={editBet.stake}
                                                    onChange={(e) => {
                                                        const stake = e.target.value;
                                                        const odds = parseFloat(editBet.odds) || 0;
                                                        const isBonus = editBet.bonus_bet;
                                                        const payout = stake && odds 
                                                            ? (isBonus ? (parseFloat(stake) * odds - parseFloat(stake)).toFixed(2) : (parseFloat(stake) * odds).toFixed(2))
                                                            : editBet.payout;
                                                        setEditBet(prev => ({ ...prev, stake, payout }));
                                                    }}
                                                    className="px-2 py-1 bg-slate-900 border border-slate-700 rounded text-sm text-white focus:outline-none focus:border-emerald-500 w-full min-w-0"
                                                />
                                            </td>
                                            <td className="px-2 py-2">
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={editBet.payout}
                                                    readOnly
                                                    className="px-2 py-1 bg-slate-900/50 border border-slate-700 rounded text-sm text-slate-400 focus:outline-none cursor-not-allowed w-full min-w-0"
                                                    title={editBet.bonus_bet ? "Auto-calculated: (Odds × Stake) - Stake" : "Auto-calculated: Stake × Odds"}
                                                />
                                            </td>
                                            <td className="px-2 py-2">
                                                <select
                                                    value={editBet.bookie}
                                                    onChange={(e) => setEditBet(prev => ({ ...prev, bookie: e.target.value }))}
                                                    className="px-2 py-1 bg-slate-900 border border-slate-700 rounded text-sm text-white focus:outline-none focus:border-emerald-500 w-full min-w-0"
                                                >
                                                    <option value="">Select</option>
                                                    {bookieOptions.map(b => (
                                                        <option key={b} value={b}>{b}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-2 py-2">
                                                <select
                                                    value={editBet.result}
                                                    onChange={(e) => setEditBet(prev => ({ ...prev, result: e.target.value }))}
                                                    className="px-2 py-1 bg-slate-900 border border-slate-700 rounded text-sm text-white focus:outline-none focus:border-emerald-500 w-full min-w-0"
                                                >
                                                    <option value="pending">Pending</option>
                                                    <option value="win">Win</option>
                                                    <option value="loss">Loss</option>
                                                    {!editBet.bonus_bet && <option value="cash out">Cash Out</option>}
                                                </select>
                                            </td>
                                            <td className="px-2 py-2 text-sm text-slate-400">
                                                <div className="flex items-center gap-1">-</div>
                                            </td>
                                            <td className="px-2 py-2 text-sm text-slate-400">-</td>
                                            <td className="px-2 py-2">
                                                <div className="flex items-center gap-2">
                                                    {editBet.bonus_bet && (
                                                        <label className="flex items-center gap-1 opacity-50 cursor-not-allowed" title="Bonus bet status cannot be changed">
                                                            <input
                                                                type="checkbox"
                                                                checked={true}
                                                                disabled
                                                                className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-900 text-emerald-500 cursor-not-allowed"
                                                            />
                                                            <span className="text-[10px] text-slate-400">Bonus</span>
                                                        </label>
                                                    )}
                                                    <button
                                                        onClick={handleUpdateBet}
                                                        disabled={isUpdating || isSaving}
                                                        className="p-1 text-emerald-400 hover:text-emerald-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {isUpdating && editingBetId === bet.id ? (
                                                            <div className="w-4 h-4 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin"></div>
                                                        ) : (
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={() => { setEditingBetId(null); setEditBet(null); }}
                                                        disabled={isUpdating}
                                                        className="p-1 text-slate-400 hover:text-white transition-colors disabled:opacity-50"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </>
                                    ) : (
                                        // View mode
                                        <>
                                            {/* Drag handle cell */}
                                            <td className="w-[40px] px-2 py-3">
                                                {canReorder ? (
                                                    <div 
                                                        className="flex items-center justify-center cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300 transition-colors"
                                                        title={`Drag to reorder (${betsByDate[bet.date]?.length || 0} bets on ${formatDate(bet.date)})`}
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" />
                                                        </svg>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center justify-center text-slate-600" title="Clear filters to enable reordering">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-300 whitespace-nowrap">
                                                {formatDate(bet.date)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-white font-medium">
                                                <div className="max-w-[150px] truncate">{bet.game}</div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-300">
                                                <div className="max-w-[350px] flex items-center gap-2">
                                                    <span>{bet.bet}</span>
                                                    {bet.bonus_bet && (
                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 whitespace-nowrap flex-shrink-0">
                                                            BONUS
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-300 font-mono">
                                                {bet.odds.toFixed(2)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-300">
                                                {formatStaked(bet.stake, bet.date)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-300">
                                                {bet.payout > 0 ? formatStaked(bet.payout, bet.date) : '-'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center justify-center">
                                                    {bookieLogos[bet.bookie] ? (
                                                        <img 
                                                            src={bookieLogos[bet.bookie]} 
                                                            alt={bet.bookie}
                                                            className="w-6 h-6 rounded object-cover"
                                                            title={bet.bookie}
                                                        />
                                                    ) : (
                                                        <span className="text-sm text-slate-300">{bet.bookie}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="relative">
                                                    {updatingResultBetId === bet.id ? (
                                                        <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-500/20 text-slate-400 border border-slate-500/30">
                                                            <div className="w-3 h-3 border-2 border-slate-400/30 border-t-slate-400 rounded-full animate-spin mr-1.5"></div>
                                                            Updating...
                                                        </div>
                                                    ) : (
                                                        <select
                                                            value={bet.result}
                                                            onChange={(e) => handleQuickResultChange(bet, e.target.value)}
                                                            disabled={isSaving || deletingBetId === bet.id || editingBetId === bet.id}
                                                            className={`appearance-none cursor-pointer px-2.5 py-1 pr-6 rounded-full text-xs font-medium border focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-slate-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                                                                bet.result === 'win' 
                                                                    ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30 focus:ring-emerald-500/50'
                                                                    : bet.result === 'loss'
                                                                        ? 'bg-red-500/20 text-red-400 border-red-500/30 focus:ring-red-500/50'
                                                                        : bet.result === 'cash out'
                                                                            ? 'bg-blue-500/20 text-blue-400 border-blue-500/30 focus:ring-blue-500/50'
                                                                            : 'bg-amber-500/20 text-amber-400 border-amber-500/30 focus:ring-amber-500/50'
                                                            }`}
                                                            style={{
                                                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                                                backgroundRepeat: 'no-repeat',
                                                                backgroundPosition: 'right 4px center',
                                                                backgroundSize: '12px'
                                                            }}
                                                        >
                                                            <option value="pending">Pending</option>
                                                            <option value="win">Win</option>
                                                            <option value="loss">Loss</option>
                                                            {!bet.bonus_bet && <option value="cash out">Cash Out</option>}
                                                        </select>
                                                    )}
                                                </div>
                                            </td>
                                            <td className={`px-4 py-3 text-sm font-semibold ${
                                                bet.pl >= 0 ? 'text-emerald-400' : 'text-red-400'
                                            }`}>
                                                {formatCurrency(bet.pl, bet.date)}
                                            </td>
                                            <td className={`px-4 py-3 text-sm font-bold ${
                                                getCumulativePLValue(bet.cumulativePL, bet.cumulativePLUnits) >= 0 ? 'text-emerald-400' : 'text-red-400'
                                            }`}>
                                                {formatCumulativePL(bet.cumulativePL, bet.cumulativePLUnits)}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => startEditing(bet)}
                                                        disabled={isSaving || deletingBetId === bet.id}
                                                        className="p-1 text-slate-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title="Edit"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteBet(bet.id)}
                                                        disabled={deletingBetId === bet.id || isSaving}
                                                        className="p-1 text-slate-400 hover:text-red-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title="Delete"
                                                    >
                                                        {deletingBetId === bet.id ? (
                                                            <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin"></div>
                                                        ) : (
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Summary Footer */}
            {processedBets.length > 0 && (
                <div className="p-4 bg-slate-800/30 border-t border-slate-700/50">
                    <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
                        <div className="text-slate-400">
                            Showing <span className="text-white font-medium">{processedBets.length}</span> of <span className="text-white font-medium">{bets.length}</span> bets
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="text-slate-400">
                                Total Staked: <span className="text-white font-medium">{formatTotalStaked(searchFilteredBets)}</span>
                            </div>
                            <div className="text-slate-400">
                                Total P/L: <span className={`font-semibold ${getTotalPLValue(searchFilteredBets) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {formatTotalPL(searchFilteredBets)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Cash Out Modal - Rendered via Portal to document.body */}
            {showCashOutModal && cashOutBet && createPortal(
                <div 
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]"
                    onClick={(e) => e.target === e.currentTarget && handleCashOutCancel()}
                >
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="text-lg font-semibold text-white">Cash Out Bet</h3>
                            <button
                                onClick={handleCashOutCancel}
                                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Bet Info */}
                        <div className="mb-4 p-3 bg-slate-800/50 rounded-xl border border-slate-700/30">
                            <div className="text-xs text-slate-500 mb-1">Bet</div>
                            <div className="text-sm text-white font-medium truncate" title={cashOutBet.bet}>
                                {cashOutBet.bet}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">{cashOutBet.game}</div>
                        </div>

                        {/* Stats Row */}
                        <div className="flex gap-3 mb-4">
                            <div className="flex-1 p-3 bg-slate-800/30 rounded-xl border border-slate-700/30">
                                <div className="text-xs text-slate-500 mb-1">Stake</div>
                                <div className="text-white font-semibold">${cashOutBet.stake.toFixed(2)}</div>
                            </div>
                            <div className="flex-1 p-3 bg-slate-800/30 rounded-xl border border-slate-700/30">
                                <div className="text-xs text-slate-500 mb-1">Potential Payout</div>
                                <div className="text-slate-300 font-semibold">${(cashOutBet.stake * cashOutBet.odds).toFixed(2)}</div>
                            </div>
                        </div>

                        {/* Cash Out Input */}
                        <div className="mb-4">
                            <label className="block text-sm text-slate-300 mb-2">Cash Out Amount</label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                                <input
                                    ref={cashOutInputRef}
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={cashOutAmount}
                                    onChange={(e) => setCashOutAmount(e.target.value)}
                                    placeholder="0.00"
                                    className="w-full pl-7 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-colors"
                                />
                            </div>
                        </div>

                        {/* P/L Preview */}
                        {cashOutAmount && !isNaN(parseFloat(cashOutAmount)) && (
                            <div className={`flex items-center justify-between p-3 rounded-xl mb-4 ${
                                parseFloat(cashOutAmount) - cashOutBet.stake >= 0 
                                    ? 'bg-emerald-500/10 border border-emerald-500/20' 
                                    : 'bg-red-500/10 border border-red-500/20'
                            }`}>
                                <span className="text-sm text-slate-300">Calculated P/L</span>
                                <span className={`font-semibold ${
                                    parseFloat(cashOutAmount) - cashOutBet.stake >= 0 
                                        ? 'text-emerald-400' 
                                        : 'text-red-400'
                                }`}>
                                    {parseFloat(cashOutAmount) - cashOutBet.stake >= 0 ? '+' : ''}
                                    ${(parseFloat(cashOutAmount) - cashOutBet.stake).toFixed(2)}
                                </span>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-3 mt-5">
                            <button
                                onClick={handleCashOutCancel}
                                className="flex-1 px-4 py-2.5 text-slate-400 hover:text-white text-sm font-medium transition-colors rounded-xl hover:bg-slate-800/50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCashOutConfirm}
                                disabled={!cashOutAmount || isNaN(parseFloat(cashOutAmount)) || parseFloat(cashOutAmount) < 0}
                                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl text-sm font-medium hover:from-emerald-500 hover:to-teal-500 transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                Confirm Cash Out
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export default BetTracker;
